// ─────────────────────────────────────────────────────────────────────────
// 좌석의 역할 — "이 코드로 들어온 사람이 학생인가 강의자인가".
//
// 왜 필요한가: 좌석은 학생 1명당 코드 1개인데 역할 필드가 없었다. 강의자가 명단에
// 한 자리 끼어 들어오면 콘솔은 그를 학생으로 안다. 그래서 세 가지가 조용히 어긋났다.
//   ① 참여율·진도의 분모에 강의자가 낀다
//   ② 강의자가 학생앱에 안 들어오면 "미접속 학생"으로 결재함에 올라간다
//      (매니저가 강의자에게 독려를 보내자고 제안한다 — 규칙 7)
//   ③ 계약 좌석 하나를 강의자가 먹는데 그게 어디에도 안 적힌다
//
// 판정을 여기 한 곳에 둔다 — 화면(Monitor·ClassDetail)과 프롬프트(lighthousePrompt)가
// 같은 함수를 쓴다. 두 벌로 두면 한쪽만 고쳐져 "화면은 12명인데 매니저는 13명이라 한다"가
// 되고, 그 순간 담당자는 둘 다 못 믿는다(규칙 1).
//
// ⚠️ 역할이 없는 옛 좌석은 **학생**이다. 이미 굴러가는 반의 집계를 바꾸지 않는다
//    (새 반부터 적용 — 담당자 결정). 그래서 판정은 'instructor' 를 명시적으로 찾는다.
//
// ⚠️ 학생이 자기를 강의자로 바꿀 수 없다 — firestore.rules 의 좌석 update 화이트리스트에
//    role 이 없다(lastActiveAt·forceResetPending·lastSeenAt·activeMs·location* 뿐).
//    새로 막을 것이 아니라 이미 막혀 있는 것이다. 화이트리스트에 role 을 추가하지 마라.
// ─────────────────────────────────────────────────────────────────────────

export const SEAT_ROLE = Object.freeze({
  student: 'student',
  instructor: 'instructor',
});

/** 좌석 문서(또는 라이선스)의 role 을 canonical 로. 모르는 값·없는 값은 학생. */
export function normalizeSeatRole(role) {
  return role === SEAT_ROLE.instructor ? SEAT_ROLE.instructor : SEAT_ROLE.student;
}

/** 강의자 좌석인가. 역할이 없는 옛 좌석은 false(=학생). */
export function isInstructorSeat(seat) {
  return !!seat && normalizeSeatRole(seat.role) === SEAT_ROLE.instructor;
}

/** 학생 좌석인가. 역할이 없는 옛 좌석은 true. */
export function isStudentSeat(seat) {
  return !!seat && !isInstructorSeat(seat);
}

/**
 * QR 로 채워지는 반인가 — **가져간 자리가 하나라도 있으면** 그렇다.
 *
 * 설정값이 아니라 데이터에서 뽑는다. joinOpen 은 담당자가 수업 뒤에 닫으므로 "쓴 반"의
 * 표시가 못 되고, 새 플래그를 하나 더 두면 그게 또 어긋난다.
 */
export function isSelfServeClass(seats = []) {
  return (Array.isArray(seats) ? seats : []).some((s) => s && s.claimedAt);
}

/**
 * 좌석 목록을 학생/강의자/빈자리로 가른다. **집계는 students 만 쓴다.**
 *
 * 왜 빈자리를 따로 세나: 담당자는 결석·중복 배정을 대비해 정원보다 좌석을 넉넉히 연다
 * (60명 반에 63석). QR 로 60명이 들어오면 3석이 안 찬다 — 그건 **사람이 아니라 빈 자리**다.
 * 학생으로 세면 참여율 분모가 틀리고, 매니저가 "3명이 한 번도 안 들어왔습니다"를 결재함에
 * 올려 **없는 사람에게 독려를 보내자고 제안한다**(규칙 7 — 손쓸 수 없는 것을 권하지 마라).
 *
 * ⚠️ 코드를 직접 나눠주는 반(claimedAt 이 아예 없는 반)은 종전 그대로다. 거기서는
 *    claimedAt 이 없는 게 정상이라, 빼기 시작하면 그 반의 집계가 통째로 사라진다.
 *    그래서 "가져간 자리가 하나라도 있는 반"에서만 가른다.
 */
export function splitSeatsByRole(seats = []) {
  const list = Array.isArray(seats) ? seats : [];
  const instructors = list.filter(isInstructorSeat);
  const students = list.filter(isStudentSeat);
  if (!isSelfServeClass(list)) return { students, instructors, vacant: [] };
  return {
    students: students.filter((s) => s.claimedAt),
    instructors,
    vacant: students.filter((s) => !s.claimedAt),
  };
}
